<template>
  <employee-profile>
    <modal-dialog-prevent-leave
      v-bind:is-shown="isShownDialogLeaveConfirm"
      v-on:back="isShownDialogLeaveConfirm = false"
      v-on:leave="leaveWithoutSave"
      v-on:save="saveAndLeave" />
    <form
      v-on:submit.prevent="onSubmitEmployeePermissionsEdit"
    >
      <v-expansion-panels
        multiple
        style="flex-direction: column"
      >
        <div class="employee-permission__collapse">
          <template v-for="(group, key) in employeePermission.permission">
            <div
              v-bind:key="key"
              v-if="group.withoutGroup"
            >
              <permission-item
                class="employee-permission__form__permission"
                v-for="permission in group.permissions"
                v-bind:key="`${permission.id}-${permission.value}`"
                v-bind:is-contains="permission.isContains"
                v-bind:name="permission.name"
                v-model="permission.value"
                v-on:input="checkChanged"
              />
            </div>
            <v-expansion-panel
              v-bind:key="key"
              v-else
            >
              <v-expansion-panel-header>
                <div>{{group.name}}</div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="employee-permission__form__group">
                  <permission-item
                    class="employee-permission__form__permission"
                    v-for="permission in group.permissions"
                    v-bind:key="`${permission.id}-${permission.value}`"
                    v-bind:is-contains="permission.isContains"
                    v-bind:name="permission.name"
                    v-model="permission.value"
                    v-on:input="checkChanged"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </template>
        </div>
      </v-expansion-panels>
      <button
        class="employee-permission__button-save"
        type="submit"
        v-bind:disabled="isAwaitRequest"
      >
        Сохранить
      </button>
    </form>
  </employee-profile>
</template>

<script>
import PermissionItem from '@/components/PermissionItem.vue';
import EmployeeProfile from '@/views/ClinicPanel/EmployeeProfile.vue';
import { EditEmployeePermissionListRequest } from '@/storage/gateways/EmployeeGateway';
import notifierMixin from '@/mixins/notifierMixin';
import ModalDialogPreventLeave from '@/components/ModalDialogPreventLeave.vue';
import preventLeaveWithoutConfirmMixin from '@/mixins/preventLeaveWithoutConfirmMixin';
import debounce from 'lodash.debounce';

/**
 * @description Права сотрудника.
 * @param {array} permission
 * @constructor
 */
function EmployeePermission(
  permission = [],
) {
  this.permission = permission;

  this.toString = () => JSON.stringify({
    permission: this.permission,
  });

  let initial = this.toString();
  this.isChanged = () => this.toString() !== initial;

  /**
   * @description Обновить.
   * @returns {void}
   */
  this.refresh = () => {
    initial = toString();
  };
}

/**
 * @description Компонент пункта профиля сотрудника "Основное".
 * @type {Vue}
 */
export default {
  computed: {
    needPreventLeave() {
      return this.isChanged;
    },
    employeeId: (vm) => parseInt(vm.$route.params.id, 10),
  },
  components: {
    ModalDialogPreventLeave,
    PermissionItem,
    EmployeeProfile,
  },
  created() {
    this.fetchProfileOptions();
    this.viewEmployeePermissions();
  },
  data() {
    return {
      employeePermission: new EmployeePermission(),
      permissionsByGroups: [],
      isAwaitRequest: false,
      isChanged: false,
    };
  },
  methods: {
    /**
     * @description Получить список прав сотрудника.
     * @returns {void}
     */
    viewEmployeePermissions() {
      const onSuccess = (permissions) => {
        this.employeePermission = new EmployeePermission(permissions);
      };
      this.$employeeGateway
        .viewPermissionListByEmployeeId(this.employeeId)
        .then((result) => result.fold(this.notifyFail, onSuccess));
    },
    /**
     * @description Получить полный список прав по группам.
     * @returns {void}
     */
    fetchProfileOptions() {
      const onResponse = (result) => {
        this.permissionsByGroups = result.permissionsByGroups;
      };
      this.$suggestionGateway.getEmployeeCreation().then(onResponse);
    },
    /**
     * @description Отправить запрос на редактирование разрешений сотрудника
     * @returns {Promise}
     */
    onSubmitEmployeePermissionsEdit() {
      const additional = [];
      const excluded = [];
      const getIds = (permissions, value) => permissions
        .filter((candidate) => candidate.value === value)
        .map((permission) => permission.id);
      this.employeePermission.permission.forEach((group) => {
        additional.push(...getIds(group.permissions, 'additional'));
        excluded.push(...getIds(group.permissions, 'excluded'));
      });
      const permissionsRequest = new EditEmployeePermissionListRequest(
        this.employeeId,
        additional,
        excluded,
      );
      const onSuccess = (result) => {
        this.notifySuccess(result.message);
        this.viewEmployeePermissions();
        this.isChanged = false;
      };
      const response = this
        .$employeeGateway
        .editPermissionListByEmployeeId(permissionsRequest);
      response.then((result) => result.fold(this.notifyFail, onSuccess));
      return response;
    },
    async saveAndLeave() {
      const result = await this.onSubmitEmployeePermissionsEdit();
      result.forEach(({ message }) => {
        this.notifySuccess(message);
        this.actionAfterClosingDialogLeaveConfirm();
      });
      this.isShownDialogLeaveConfirm = false;
    },
    /**
     * @description Проверить наличие изменений.
     * @returns {void}
     */
    checkChanged: debounce(function _() {
      this.isChanged = this.employeePermission.isChanged();
    }, 300),
  },
  mixins: [
    notifierMixin,
    preventLeaveWithoutConfirmMixin,
  ],
  name: 'ClinicPanelEmployeeProfilePermissions',
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.employee-permission {
  &__collapse {
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: 100%;
    width: 100%;
    max-width: 700px;
    .v-expansion-panel:first-child {
      margin: 37px 0 0 0;
    }
    .v-expansion-panel:before {
      box-shadow: none;
    }
    .v-expansion-panel-header {
      padding: 0;
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      color: #192a3e;
      min-height: 25px;
    }
    .v-expansion-panel:not(:first-child):after {
      border-top: none;
    }
    .v-expansion-panel-content__wrap {
      padding: 0;
    }
    .v-expansion-panel-content {
      margin: 15px 0 0 0;
    }
  }
  &__button-save {
    @include buttons.btn-blue(100%, 48px, 16px);
    max-width: 174px;
    margin: 20px 0 0 0 !important;
  }
  &__form {
    display: flex;
    flex-direction: column;
    gap: 10px;
    &__group {
      display: flex;
      flex-direction: column;
      gap: 5px;
      cursor: default;
    }
    &__permission {
      margin-left: 15px;
    }
  }
}
</style>
